import { useEffect, useState } from 'react';
import { Container, VStack, Heading, Text, Button, RadioGroup, Radio, Stack, Alert, AlertIcon, Spinner, Box } from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import { api } from '../lib/api';

interface QuestionData {
  id: number;
  text: string;
  options: { key: 'A'|'B'|'C'|'D'|'E'; text: string }[];
  correctAnswer: 'A'|'B'|'C'|'D'|'E';
  explanation: string;
  topic?: { name: string };
}

export default function QuestionPage() {
  const [q, setQ] = useState<QuestionData | null>(null);
  const [picked, setPicked] = useState<string>('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchOne = async () => {
    setLoading(true); setSubmitted(false); setPicked(''); setError(null);
    try {
      setQ(await api.randomQuestion());
    } catch (e: any) {
      if (e?.status === 429) setError('Günlük limit doldu. Aboneliğe geç veya kontör al.');
      else setError('Soru çekilemedi.');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchOne(); }, []);

  if (loading) return <VStack p={12}><Spinner /></VStack>;
  if (error) return (
    <Container maxW="md" py={6}>
      <Alert status="warning" borderRadius="md"><AlertIcon />{error}</Alert>
    </Container>
  );
  if (!q) return null;

  const correct = submitted && picked === q.correctAnswer;
  return (
    <Container maxW="md" py={6}>
      <VStack spacing={4} align="stretch">
        {q.topic && <Text fontSize="sm" color="textMuted">{q.topic.name}</Text>}
        <Heading size="md" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(q.text) }} />

        <RadioGroup value={picked} onChange={setPicked} isDisabled={submitted}>
          <Stack>
            {q.options.map(o => {
              const isCorrect = submitted && o.key === q.correctAnswer;
              const isWrongPicked = submitted && o.key === picked && picked !== q.correctAnswer;
              return (
                <Box
                  key={o.key}
                  p={3}
                  borderRadius="md"
                  border="1px solid"
                  borderColor={
                    isCorrect ? 'optionCorrectBorderColor'
                      : isWrongPicked ? 'optionIncorrectBorderColor'
                      : 'optionDefaultBorderColor'
                  }
                  bg={
                    isCorrect ? 'optionCorrectBg'
                      : isWrongPicked ? 'optionIncorrectBg'
                      : 'optionDefaultBg'
                  }
                  color={isCorrect || isWrongPicked ? 'white' : undefined}
                >
                  <Radio value={o.key}>
                    <Text as="span" fontWeight="bold" mr={2}>{o.key})</Text>
                    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(o.text) }} />
                  </Radio>
                </Box>
              );
            })}
          </Stack>
        </RadioGroup>

        {!submitted ? (
          <Button colorScheme="brand" isDisabled={!picked} onClick={() => setSubmitted(true)}>
            Cevapla
          </Button>
        ) : (
          <>
            <Alert status={correct ? 'success' : 'error'} borderRadius="md">
              <AlertIcon />
              {correct ? 'Doğru!' : `Doğru cevap: ${q.correctAnswer}`}
            </Alert>
            <Box
              bg="bgExplanation" border="1px solid" borderColor="borderExplanation"
              p={3} borderRadius="md"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(q.explanation) }}
            />
            <Button onClick={fetchOne}>Yeni soru</Button>
          </>
        )}
      </VStack>
    </Container>
  );
}
